<template>
  <div class="container admin-results">
    <h1 class="hover" @click="$router.push('/admin/events/' + eventId + '/registrations')">← Voltar ao evento</h1>
    <h2>Resultados &amp; Certificados</h2>

    <div v-if="loading" class="muted">Carregando...</div>

    <template v-else>
      <!-- ============ RESULTADOS ============ -->
      <section class="panel">
        <div class="panel-head">
          <h3>Resultados</h3>
          <span>
            <span v-if="msg" class="msg">{{ msg }}</span>
            <button class="btn btn-secondary" :disabled="busy" @click="recompute">Recalcular colocações</button>
          </span>
        </div>
        <p class="muted small">
          Registre o <strong>tempo</strong> e o status de cada inscrição. As colocações
          (geral na prova e por categoria) são calculadas automaticamente pelo tempo.
        </p>

        <div v-for="comp in competitions" :key="comp.competitionId" class="comp">
          <h4>{{ comp.competitionName }}</h4>
          <div class="table-wrap">
            <table class="results-table">
              <thead>
                <tr>
                  <th class="sortable" title="Nº da inscrição (mesmo do export em tabela)" @click="sortBy('id')">Nº<span class="arrow">{{ arrow('id') }}</span></th>
                  <th class="sortable" @click="sortBy('name')">Inscrição<span class="arrow">{{ arrow('name') }}</span></th>
                  <th class="sortable" @click="sortBy('category')">Categoria<span class="arrow">{{ arrow('category') }}</span></th>
                  <th class="sortable" @click="sortBy('time')">Tempo (h:mm:ss)<span class="arrow">{{ arrow('time') }}</span></th>
                  <th class="sortable" @click="sortBy('status')">Status<span class="arrow">{{ arrow('status') }}</span></th>
                  <th class="sortable" @click="sortBy('overall')">Geral<span class="arrow">{{ arrow('overall') }}</span></th>
                  <th class="sortable" @click="sortBy('catplc')">Categoria<span class="arrow">{{ arrow('catplc') }}</span></th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in sortedEntries(comp)" :key="e.purchaseProductId">
                  <td class="id">{{ e.purchaseProductId }}</td>
                  <td class="name">{{ e.name }}</td>
                  <td>{{ e.categoryName }}</td>
                  <td><input class="time" v-model="e._timeStr" placeholder="--:--:--" /></td>
                  <td>
                    <select v-model="e._status">
                      <option :value="null">— sem resultado —</option>
                      <option value="finished">Concluído</option>
                      <option value="dnf">DNF (não terminou)</option>
                      <option value="dq">DQ (desclassificado)</option>
                    </select>
                  </td>
                  <td class="plc">{{ e.overallPlacement != null ? e.overallPlacement + 'º' : '—' }}</td>
                  <td class="plc">{{ e.categoryPlacement != null ? e.categoryPlacement + 'º' : '—' }}</td>
                  <td class="actions">
                    <button class="btn btn-sm btn-primary" :disabled="busy" @click="saveResult(e)">Salvar</button>
                    <button class="btn btn-sm btn-light cert-btn" :disabled="busy"
                            title="Abrir o certificado desta inscrição em nova aba"
                            @click="openCertificate(e.purchaseProductId)">🏅 Certificado</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p v-if="!competitions.length" class="muted">Nenhuma inscrição paga encontrada neste evento.</p>
      </section>

      <!-- ============ CERTIFICADO ============ -->
      <section class="panel">
        <h3>Certificado</h3>
        <label class="toggle"><input type="checkbox" v-model="cert.certificateEnabled" /> Habilitar certificados neste evento</label>
        <label class="toggle"><input type="checkbox" v-model="cert.resultsPublished" /> Publicar resultados (participante vê colocação e baixa o certificado)</label>

        <div class="tpl">
          <div class="tpl-head">
            <strong>Modelo (HTML com placeholders)</strong>
            <button class="btn btn-sm btn-light" @click="insertDefault">Inserir modelo padrão</button>
          </div>
          <p class="muted small">
            Placeholders: <code v-pre>{{nome}}</code> <code v-pre>{{nome_equipe}}</code>
            <code v-pre>{{atleta}}</code> <code v-pre>{{participante}}</code> (“o atleta” / “a equipe”)
            <code v-pre>{{prova}}</code> <code v-pre>{{prova_nome}}</code> (prova sem a distância)
            <code v-pre>{{distancia}}</code> (ex.: 100 KM) <code v-pre>{{categoria}}</code>
            <code v-pre>{{tempo}}</code> <code v-pre>{{colocacao_geral}}</code>
            <code v-pre>{{colocacao_categoria}}</code> <code v-pre>{{n_atletas}}</code>
            <code v-pre>{{evento}}</code> <code v-pre>{{data}}</code>
            <code v-pre>{{bg}}</code> (arte de fundo, ver abaixo)
          </p>
          <textarea v-model="cert.certificateTemplate" class="tpl-area" spellcheck="false"></textarea>
          <label class="bg-key">
            <strong>Arte de fundo</strong> <span class="muted small">— nome do arquivo em <code>server/assets/certificates/</code> ou uma URL; entra no modelo como <code v-pre>{{bg}}</code></span>
            <input type="text" v-model="cert.certificateBgKey" placeholder="ex.: volta-do-lago-2026.jpg" />
          </label>
        </div>

        <div class="cert-actions">
          <button class="btn btn-primary" :disabled="busy" @click="saveCert">Salvar certificado</button>
          <select v-model="previewPpId" class="preview-pick">
            <option :value="null">pré-visualizar com…</option>
            <optgroup v-for="comp in competitions" :key="comp.competitionId" :label="comp.competitionName">
              <option v-for="e in comp.entries" :key="e.purchaseProductId" :value="e.purchaseProductId">{{ e.name }}</option>
            </optgroup>
          </select>
          <button class="btn btn-secondary" :disabled="!previewPpId || busy" @click="preview">Pré-visualizar</button>
        </div>
        <span v-if="msg" class="msg">{{ msg }}</span>
      </section>
    </template>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'admin-results',

  data() {
    return {
      loading: true, busy: false, msg: '',
      competitions: [],
      cert: { certificateEnabled: false, certificateTemplate: '', resultsPublished: false, certificateBgKey: '' },
      previewPpId: null,
      sort: { key: null, dir: 1 }, // null = ordem do servidor (categoria, tempo)
    };
  },

  computed: {
    eventId() { return parseInt(this.$route.params.eventid); },
  },

  async created() {
    await Promise.all([this.loadResults(), this.loadCert()]);
    this.loading = false;
  },

  methods: {
    sortBy(key) {
      if (this.sort.key === key) {
        if (this.sort.dir === 1) this.sort.dir = -1;
        else this.sort = { key: null, dir: 1 }; // 3º clique volta à ordem padrão
      } else {
        this.sort = { key, dir: 1 };
      }
    },
    arrow(key) {
      if (this.sort.key !== key) return '';
      return this.sort.dir === 1 ? ' ▲' : ' ▼';
    },
    sortedEntries(comp) {
      const { key, dir } = this.sort;
      if (!key) return comp.entries;
      const get = {
        id: (e) => e.purchaseProductId,
        name: (e) => (e.name || '').toLowerCase(),
        category: (e) => (e.categoryName || '').toLowerCase(),
        time: (e) => e.finishTimeSeconds,
        status: (e) => e.resultStatus || '',
        overall: (e) => e.overallPlacement,
        catplc: (e) => e.categoryPlacement,
      }[key];
      return comp.entries.slice().sort((a, b) => {
        const va = get(a), vb = get(b);
        const na = va == null || va === '', nb = vb == null || vb === '';
        if (na && nb) return 0;
        if (na) return 1; // vazios sempre no fim
        if (nb) return -1;
        const c = typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'pt-BR');
        return c * dir;
      });
    },

    secsToHms(s) {
      if (s == null || !isFinite(s)) return '';
      s = Math.round(Number(s));
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
      const p = (n) => String(n).padStart(2, '0');
      return h + ':' + p(m) + ':' + p(ss);
    },
    hmsToSecs(str) {
      if (!str || !str.trim()) return null;
      const parts = str.trim().split(':').map((x) => parseInt(x, 10));
      if (parts.some((x) => isNaN(x))) return NaN;
      let h = 0, m = 0, s = 0;
      if (parts.length === 3) [h, m, s] = parts;
      else if (parts.length === 2) [m, s] = parts;
      else [s] = parts;
      return h * 3600 + m * 60 + s;
    },

    async loadResults() {
      const r = await api.getJSONAsync('resultsAdminList', { eventId: this.eventId });
      const comps = (r && r.competitions) || [];
      comps.forEach((c) => c.entries.forEach((e) => {
        this.$set(e, '_timeStr', this.secsToHms(e.finishTimeSeconds));
        this.$set(e, '_status', e.resultStatus || null);
      }));
      this.competitions = comps;
    },
    async loadCert() {
      const r = await api.getJSONAsync('certificateConfigGet', { eventId: this.eventId });
      if (r && !r.error) {
        this.cert.certificateEnabled = !!r.certificateEnabled;
        this.cert.certificateTemplate = r.certificateTemplate || '';
        this.cert.resultsPublished = !!r.resultsPublished;
        this.cert.certificateBgKey = r.certificateBgKey || '';
      }
    },

    async saveResult(e) {
      const secs = this.hmsToSecs(e._timeStr);
      if (Number.isNaN(secs)) { this.msg = 'Tempo inválido (use h:mm:ss).'; return; }
      // Tempo preenchido sem status → assume Concluído.
      let status = e._status;
      if (secs != null && !status) status = 'finished';
      this.busy = true; this.msg = '';
      try {
        const r = await api.postJSONAsync('resultsAdminSet', {
          purchaseProductId: e.purchaseProductId, finishTimeSeconds: secs, resultStatus: status,
        });
        if (r && r.error) throw new Error(r.message);
        await this.loadResults(); // recarrega colocações recalculadas
        this.msg = 'Resultado salvo ✓';
      } catch (err) { this.msg = 'Erro: ' + (err.message || err); }
      finally { this.busy = false; }
    },
    async recompute() {
      this.busy = true; this.msg = '';
      try {
        await api.postJSONAsync('resultsAdminRecompute', { eventId: this.eventId });
        await this.loadResults();
        this.msg = 'Colocações recalculadas ✓';
      } finally { this.busy = false; }
    },

    async saveCert() {
      this.busy = true; this.msg = '';
      try {
        const r = await api.postJSONAsync('certificateConfigSet', {
          eventId: this.eventId,
          certificateEnabled: this.cert.certificateEnabled,
          certificateTemplate: this.cert.certificateTemplate,
          resultsPublished: this.cert.resultsPublished,
          certificateBgKey: this.cert.certificateBgKey,
        });
        if (r && r.error) throw new Error(r.message);
        this.msg = 'Certificado salvo ✓';
      } catch (err) { this.msg = 'Erro: ' + (err.message || err); }
      finally { this.busy = false; }
    },
    async openCertificate(ppId) {
      if (!ppId) return;
      // Abre a aba já no clique: depois do await o navegador trataria como pop-up.
      const w = window.open('', '_blank');
      if (w) w.document.write('<p style="font-family:sans-serif;padding:2em;color:#555">Gerando certificado…</p>');
      this.busy = true; this.msg = '';
      try {
        const r = await api.getJSONAsync('certificateGet', { purchaseProductId: ppId });
        if (!r || r.error || !r.html) throw new Error((r && r.message) || 'Certificado indisponível');
        if (!w) throw new Error('O navegador bloqueou a nova aba — permita pop-ups para este site.');
        w.document.open(); w.document.write(r.html); w.document.close();
      } catch (err) {
        if (w) w.close();
        this.msg = 'Erro: ' + (err.message || err);
      } finally { this.busy = false; }
    },
    preview() { this.openCertificate(this.previewPpId); },
    insertDefault() {
      if (this.cert.certificateTemplate && this.cert.certificateTemplate.trim()
          && !confirm('Substituir o modelo atual pelo padrão?')) return;
      this.cert.certificateTemplate = DEFAULT_TEMPLATE;
    },
  },
};

const DEFAULT_TEMPLATE = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Certificado</title>
<style>
  @page { size: A4 landscape; margin: 0; }
  html,body { margin:0; padding:0; }
  .cert {
    box-sizing:border-box; width:297mm; height:210mm; margin:0 auto;
    padding:28mm 26mm; text-align:center;
    font-family: Georgia, 'Times New Roman', serif; color:#1f2d3d;
    background:#ffffff;
    border:10px solid #17607a; outline:2px solid #17607a; outline-offset:8px;
    display:flex; flex-direction:column; justify-content:center;
  }
  .cert h1 { font-size:34pt; letter-spacing:2px; margin:0 0 6mm; color:#17607a; }
  .cert .sub { font-size:13pt; color:#555; margin-bottom:10mm; }
  .cert .nome { font-size:30pt; font-weight:bold; margin:6mm 0; }
  .cert .linha { font-size:15pt; margin:2mm 0; }
  .cert .destaque { color:#17607a; font-weight:bold; }
  .cert .rodape { margin-top:14mm; font-size:11pt; color:#777; }
  @media screen { body { background:#e9edf0; padding:16px; } }
</style></head>
<body>
  <div class="cert">
    <h1>CERTIFICADO</h1>
    <div class="sub">{{evento}}</div>
    <div class="linha">Certificamos que</div>
    <div class="nome">{{nome}}</div>
    <div class="linha">concluiu a prova <span class="destaque">{{prova}}</span> — categoria <span class="destaque">{{categoria}}</span></div>
    <div class="linha">com o tempo de <span class="destaque">{{tempo}}</span></div>
    <div class="linha">Colocação geral: <span class="destaque">{{colocacao_geral}}</span> &nbsp;·&nbsp; na categoria: <span class="destaque">{{colocacao_categoria}}</span></div>
    <div class="rodape">{{data}}</div>
  </div>
</body></html>`;
</script>

<style scoped lang="stylus">
.admin-results
  padding: 1.5em 0

.panel
  margin: 1em 0 1.5em
  padding: 1em 1.2em
  border: 1px solid rgba(0,0,0,0.12)
  border-radius: 8px

.panel-head
  display: flex
  justify-content: space-between
  align-items: center

.comp
  margin-top: 1em
  h4
    margin: 0 0 0.4em

.table-wrap
  overflow-x: auto

.results-table
  width: 100%
  border-collapse: collapse
  font-size: 0.88em

  th, td
    padding: 0.35em 0.5em
    border-bottom: 1px solid rgba(0,0,0,0.08)
    text-align: left

  th.sortable
    cursor: pointer
    user-select: none
    white-space: nowrap
    &:hover
      color: #17607a
    .arrow
      font-size: 0.8em

  td.id
    color: #666
    font-variant-numeric: tabular-nums

  td.name
    font-weight: 600

  td.plc
    text-align: center
    font-weight: 700
    color: #17607a

  td.actions
    white-space: nowrap
    .cert-btn
      margin-left: 0.4em

  input.time
    width: 90px
    padding: 0.25em 0.4em
    border: 1px solid #ccc
    border-radius: 4px
    text-align: center

  select
    padding: 0.25em
    border: 1px solid #ccc
    border-radius: 4px

.toggle
  display: block
  margin: 0.3em 0
  font-weight: 600

.tpl
  margin: 0.8em 0

.tpl-head
  display: flex
  justify-content: space-between
  align-items: center

.tpl-area
  width: 100%
  min-height: 260px
  font-family: monospace
  font-size: 0.8em
  padding: 0.6em
  border: 1px solid #ccc

.bg-key
  display: block
  margin-top: 0.6em
  input
    display: block
    width: 100%
    max-width: 480px
    margin-top: 0.3em
    padding: 0.35em 0.5em
    border: 1px solid #ccc
    border-radius: 4px
  border-radius: 6px

.cert-actions
  display: flex
  gap: 0.6em
  align-items: center
  flex-wrap: wrap

.preview-pick
  padding: 0.4em
  border: 1px solid #ccc
  border-radius: 4px

.btn
  padding: 0.45em 1em
  border-radius: 5px
  border: none
  cursor: pointer
  font-size: 0.9em
.btn-sm
  padding: 0.3em 0.7em
  font-size: 0.82em
.btn-primary
  background: #007bff
  color: #fff
.btn-secondary
  background: #17a2b8
  color: #fff
.btn-light
  background: #eee
.btn:disabled
  opacity: 0.5
  cursor: default

.msg
  margin-left: 0.6em
  color: #0f5132
  font-size: 0.9em
.muted
  color: #999
.small
  font-size: 0.85em
.hover
  cursor: pointer
</style>
